import React from "react";
import { Helmet } from "react-helmet-async";
import { useLanguage } from "../LanguageContext";
import { DOMAIN, LOCALES, buildUrl, BRAND } from "../seo/seoConfig";
import { isSpanishOnly, localizePath } from "../seo/routes";

const LANG_HTML = { ES: "es", EN: "en", PT: "pt" };

// Reusable SEO head manager: title, description, canonical, hreflang alternates,
// Open Graph, Twitter and JSON-LD structured data.
export const Seo = ({
  title,
  description,
  path = "/",
  image = BRAND.image,
  type = "website",
  schema = [],
  noindex = false,
}) => {
  const { lang } = useLanguage();
  const esOnly = isSpanishOnly(path);
  const urlFor = (l) => buildUrl(esOnly ? path : localizePath(path, l));
  const canonical = urlFor(esOnly ? "ES" : lang);
  const schemas = Array.isArray(schema) ? schema : [schema];

  return (
    <Helmet>
      <html lang={LANG_HTML[lang]} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex ? <meta name="robots" content="noindex,follow" /> : <meta name="robots" content="index,follow,max-image-preview:large" />}
      <link rel="canonical" href={canonical} />

      {LOCALES.filter((l) => !esOnly || l.lang === "ES").map((l) => (
        <link key={l.hreflang} rel="alternate" hrefLang={l.hreflang} href={urlFor(l.lang)} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={urlFor("ES")} />

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={BRAND.name} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={lang === "PT" ? "pt_BR" : lang === "EN" ? "en_US" : "es_AR"} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {schemas.filter(Boolean).map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
};

export default Seo;
export { DOMAIN };
